import React, {useEffect, useState} from 'react';
import ContactData from '../component/contact-data';
import Wrapper from '../component/Wrapper';
import {useDispatch, useSelector} from 'react-redux';
import {selectContactUsData} from '../store/selectors';
import {getContactUsData} from '../store/actions/app';
import {toast} from 'react-toastify';
import {useForm} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {useLocalizedData} from '../hooks/useTranslatedContent';
import {useTranslation} from 'react-i18next';
import axios from "axios";
import Preloader from "../component/Preloader";
import Error404 from "../component/404";

export default function ContactUs() {
    const dispatch = useDispatch();
    const contactUsData = useSelector(selectContactUsData);
    const status = useSelector(state => state.app.status.contactUsData);
    const {getLocalized} = useLocalizedData(contactUsData);
    const {t} = useTranslation();
    const schema = yup.object().shape({
        name: yup
            .string()
            .trim()
            .required(t('contact.requiredName'))
            .min(2, t('contact.nameTooShort') || 'Name must be at least 2 characters')
            .max(20, t('contact.nameTooLong') || 'Name must be no more than 20 characters')
            .matches(
                /^[\p{L}\s'-]+$/u,
                t('contact.invalidName') || 'Please enter a valid name (letters only)'
            )
            .test('not-only-spaces', t('contact.requiredName'), value => value.trim().length > 0),

        email: yup
            .string()
            .email(t('contact.invalidEmail'))
            .trim()
            .required(t('contact.requiredEmail')),

        comments: yup
            .string()
            .trim()
            .required(t('contact.requiredMessage'))
            .min(2, t('contact.messageTooShort') || 'Message must be at least 2 characters')
            .max(500, t('contact.messageTooLong') || 'Message must be no more than 500 characters')
            .test('not-only-spaces', t('contact.requiredMessage'), value => value.trim().length > 0)
    });

    const {
        register,
        handleSubmit,
        reset,
        formState: {errors},
    } = useForm({
        resolver: yupResolver(schema),
    });


    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (status === 'idle') {
            dispatch(getContactUsData());
        }
    }, [dispatch, status]);

    if (status === 'loading' || status === 'idle') {
        return <Preloader />;
    }

    if (status === 'failed' || !contactUsData) {
        return <Error404 />;
    }

    const firstBanner = getLocalized('firstBanner') || '/default-contact-banner.jpg';
    const bannerDesc = getLocalized('bannerDesc') || t('contact.getInTouch');
    const titleContact = getLocalized('titleContact') || '';
    const secondBanner = getLocalized('secondBanner') || '';

    const onSubmit = async (data) => {
        setIsSubmitting(true);
        try {
            const response = await axios.post('/somnium/messages', data);
            if (response.status === 200) {
                toast.success(t('contact.success'));
                reset();
            } else {
                toast.error(t('contact.error'));
            }
        } catch (error) {
            toast.error(error.response?.data?.message || t('contact.error'));
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Wrapper>
            <section
                className="py-40 w-full relative bg-no-repeat bg-cover bg-center"
                style={{
                    backgroundImage: `url(${firstBanner})`,
                }}
                id="home"
            >
                <div className="absolute inset-0 bg-black bg-opacity-50"></div>

                <div className="container relative flex flex-col justify-start items-start">
                    <h1 className={'font-medium leading-normal text-5xl text-white'}>{titleContact}</h1>
                    <p
                        className="text-2xl text-white mt-4 max-w-3xl"
                        dangerouslySetInnerHTML={{ __html: bannerDesc }}
                    />
                </div>
            </section>

            <section className="relative lg:py-24 py-16">
                <div className="container">
                    {secondBanner ? (
                        <div className="grid md:grid-cols-12 grid-cols-1 items-center gap-[30px]">
                            <div className="lg:col-span-7 md:col-span-6">
                                <img src={secondBanner} alt={""} className="w-[500px] h-[650px]" />
                            </div>

                            <div className="lg:col-span-5 md:col-span-6">
                                <div className="lg:ms-5">
                                    {renderForm()}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="flex justify-center">
                            <div className="max-w-lg w-full">
                                {renderForm()}
                            </div>
                        </div>
                    )}
                </div>
                <ContactData contactUsData={contactUsData} />
            </section>
        </Wrapper>
    );

    function renderForm() {
        return (
            <div className="bg-white dark:bg-slate-900 rounded-md shadow dark:shadow-gray-700 p-6">
                <h3 className="mb-6 text-2xl leading-normal font-medium">
                    {getLocalized('contactFormTitle') || t('contact.contactFormTitle')}
                </h3>

                <form onSubmit={handleSubmit(onSubmit)} noValidate>
                    <div className="grid lg:grid-cols-12 lg:gap-6">
                        <div className="lg:col-span-6 mb-5">
                            <label htmlFor="name" className="form-label font-medium">
                                {getLocalized('nameLabel') || t('contact.nameLabel')}
                            </label>
                            <input
                                {...register('name')}
                                id="name"
                                type="text"
                                className={`form-input w-full py-2 px-3 h-10 bg-transparent border ${
                                    errors.name ? 'border-red-500' : 'border-inherit'
                                } dark:border-gray-800 dark:bg-slate-900 dark:text-slate-200 rounded outline-none focus:ring-0 mt-2`}
                                placeholder={getLocalized('namePlaceholder') || t('contact.namePlaceholder')}
                                maxLength={50}
                            />
                            {errors.name && (
                                <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
                            )}
                        </div>

                        <div className="lg:col-span-6 mb-5">
                            <label htmlFor="email" className="form-label font-medium">
                                {getLocalized('emailLabel') || t('contact.emailLabel')}
                            </label>
                            <input
                                {...register('email')}
                                id="email"
                                type="email"
                                className={`form-input w-full py-2 px-3 h-10 bg-transparent border ${
                                    errors.email ? 'border-red-500' : 'border-inherit'
                                } dark:border-gray-800 dark:bg-slate-900 dark:text-slate-200 rounded outline-none focus:ring-0 mt-2`}
                                placeholder={getLocalized('emailPlaceholder') || t('contact.emailPlaceholder')}
                            />
                            {errors.email && (
                                <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>
                            )}
                        </div>
                    </div>

                    <div className="mb-5">
                        <label htmlFor="comments" className="form-label font-medium">
                            {getLocalized('messageLabel') || t('contact.messageLabel')}
                        </label>
                        <textarea
                            {...register('comments')}
                            id="comments"
                            className={`form-input w-full py-2 px-3 bg-transparent border ${
                                errors.comments ? 'border-red-500' : 'border-inherit'
                            } dark:border-gray-800 dark:bg-slate-900 dark:text-slate-200 rounded outline-none focus:ring-0 mt-2 textarea h-28`}
                            placeholder={getLocalized('messagePlaceholder') || t('contact.messagePlaceholder')}
                            maxLength={500}
                        ></textarea>
                        {errors.comments && (
                            <p className="text-red-500 text-sm mt-1">{errors.comments.message}</p>
                        )}
                    </div>

                    <button
                        type="submit"
                        disabled={isSubmitting}
                        className={`py-2 px-5 inline-block font-normal tracking-wide border align-middle transition duration-500 ease-in-out text-base text-center hover:bg-black-700 border-black-600 hover:border-black-700 text-white rounded-md
                        ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                        style={{ background: 'black' }}
                    >
                        {isSubmitting
                            ? getLocalized('sendingText') || t('contact.sendingText')
                            : getLocalized('sendButtonText') || t('contact.sendButtonText')}
                    </button>
                </form>
            </div>
        );
    }
}
